import React, { useEffect, useState } from 'react';
import type { FunctionComponent } from 'react';
import type { IProject, Translation } from '../../interface/Localization';
import type { AxiosError } from 'axios';
import {
    InputGroup,
    InputLeftAddon,
    Input,
    InputRightAddon,
    Box,
    useToast,
    Button,
    Flex,
    MenuItem,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
} from '@chakra-ui/react';
import { Request as apiRequest } from '../../helper';
import { ActionMenu } from '../../components/ActionMenu';
import { IoArrowBack } from 'react-icons/io5';
import { MdKeyboardArrowRight } from 'react-icons/md';

// # Prefix
enum FormOfAddressIdentifier {
    formal,
    informal,
}

// ! Prefix
enum PlatformEnvironmentIdentifier {}

// ^ Prefix
enum PlatformVendorIdentifier {
    Google,
    Huawei,
}

// ~ Prefix
enum PlatformIdentifier {
    Android,
    iOS,
    OSX,
    Windows,
}

// @ Prefix
enum LanguageIdentifier {}

interface Props {
    translationData: Translation[];
    projectId: string;
    onVariationCreated?: () => void | Promise<void>;
}

interface CreateLocalizationKeyPayload {
    name: string;
    values?: Array<{
        language: string;
        value: string;
    }>;
}

const buildCreateLocalizationKeyPayload = (name: string, drafts: Translation[]): CreateLocalizationKeyPayload => {
    const valuesByLanguage = new Map<string, string>();

    drafts.forEach((draft) => {
        const language = draft.language?.trim();
        const value = draft.value ?? '';

        if (!language || !value.trim() || valuesByLanguage.has(language)) {
            return;
        }

        valuesByLanguage.set(language, value);
    });

    const values = Array.from(valuesByLanguage.entries()).map(([language, value]) => ({
        language,
        value,
    }));

    return {
        name: name.trim(),
        ...(values.length > 0 ? { values } : {}),
    };
};

const toastCreateKeyError = (toast: ReturnType<typeof useToast>, error: unknown): void => {
    const status = (error as AxiosError)?.response?.status;

    if (status === 404) {
        toast({
            title: 'Project not found.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (status === 409) {
        toast({
            title: 'Translation key already exists.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (status === 400) {
        toast({
            title: 'Invalid key payload or unknown language.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    toast({
        title: 'Error creating translation variation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
    });
};

export const FormComponent: FunctionComponent<Props> = ({ translationData, projectId, onVariationCreated }) => {
    const [translationValue, setTranslationValue] = useState<Translation[]>(
        [...translationData].sort((a, b) => (a.language_code ?? '').localeCompare(b.language_code ?? '')),
    );
    const [selectedEnumGroup, setSelectedEnumGroup] = useState<string | null>(null);
    const [isCreateVariationDialogOpen, setIsCreateVariationDialogOpen] = useState<boolean>(false);
    const [variationDrafts, setVariationDrafts] = useState<Translation[]>([]);
    const [variationKey, setVariationKey] = useState<string>('');
    const [isSavingVariation, setIsSavingVariation] = useState<boolean>(false);
    const toast = useToast();

    const enumGroups: Array<{ name: string; prefix: string; index: number; values: string[] }> = [
        {
            name: 'FormOfAddressIdentifier',
            prefix: '#',
            index: 0,
            values: Object.keys(FormOfAddressIdentifier).filter((key) => Number.isNaN(Number(key))),
        },
        {
            name: 'PlatformEnvironmentIdentifier',
            prefix: '!',
            index: 1,
            values: Object.keys(PlatformEnvironmentIdentifier).filter((key) => Number.isNaN(Number(key))),
        },
        {
            name: 'PlatformVendorIdentifier',
            prefix: '^',
            index: 2,
            values: Object.keys(PlatformVendorIdentifier).filter((key) => Number.isNaN(Number(key))),
        },
        {
            name: 'PlatformIdentifier',
            prefix: '~',
            index: 3,
            values: Object.keys(PlatformIdentifier).filter((key) => Number.isNaN(Number(key))),
        },
        {
            name: 'LanguageIdentifier',
            prefix: '@',
            index: 4,
            values: Object.keys(LanguageIdentifier).filter((key) => Number.isNaN(Number(key))),
        },
    ];

    useEffect(() => {
        setTranslationValue(
            [...translationData].sort((a, b) => (a.language_code ?? '').localeCompare(b.language_code ?? '')),
        );
    }, [translationData]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { id, value } = event.target;
        setTranslationValue((prevData) =>
            prevData.map((trans) => (trans.id + '/' + trans.language === id ? { ...trans, value } : trans)),
        );
    };

    const saveInput = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        const saveNewValue = async (translation: Translation): Promise<void> => {
            await apiRequest.post<IProject>({
                path: '/localization/project/' + (projectId ?? translation.project_id),
                data: translation,
            });
        };
        for (const translation of translationValue) {
            if (translation.value !== translation.placeholder) {
                await saveNewValue(translation);
                setTranslationValue((prevData) =>
                    prevData.map((trans) =>
                        trans.id === translation.id && trans.language === translation.language
                            ? { ...trans, placeholder: translation.value }
                            : trans,
                    ),
                );
            }
        }
    };

    const saveToClipboard = (key: string): void => {
        navigator.clipboard
            .writeText(key)
            .then(() => {
                toast({
                    title: 'Copied to clipboard.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            })
            .catch(() => {
                toast({
                    title: 'Error',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            });
    };

    const buildVariationKey = (key: string, prefix: string, enumValue: string): string =>
        `${key}${prefix}.${enumValue}`;

    const openCreateVariationDialog = (groupPrefix: string, enumValue: string): void => {
        const baseTranslation = translationValue[0];

        if (!baseTranslation) {
            return;
        }

        const nextVariationKey = buildVariationKey(baseTranslation.key, groupPrefix, enumValue);
        setVariationKey(nextVariationKey);
        setVariationDrafts(
            translationValue.map((translation) => ({
                ...translation,
                key: nextVariationKey,
            })),
        );
        setIsCreateVariationDialogOpen(true);
    };

    const closeCreateVariationDialog = (): void => {
        setIsCreateVariationDialogOpen(false);
        setVariationDrafts([]);
        setVariationKey('');
    };

    const handleVariationValueChange = (id: string, language: string, value: string): void => {
        setVariationDrafts((prevData) =>
            prevData.map((translation) =>
                translation.id === id && translation.language === language ? { ...translation, value } : translation,
            ),
        );
    };

    const saveVariationTranslations = async (): Promise<void> => {
        const targetProjectId = (projectId ?? variationDrafts[0]?.project_id ?? '').trim();

        if (variationDrafts.length === 0 || !variationKey.trim() || !targetProjectId) {
            return;
        }

        setIsSavingVariation(true);

        try {
            await apiRequest.post<IProject>({
                path: '/localization/project/' + targetProjectId + '/key',
                data: buildCreateLocalizationKeyPayload(variationKey, variationDrafts),
            });

            toast({
                title: 'Translation variation created.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            closeCreateVariationDialog();
            setSelectedEnumGroup(null);
            if (onVariationCreated) {
                await onVariationCreated();
            }
        } catch (error) {
            toastCreateKeyError(toast, error);
        } finally {
            setIsSavingVariation(false);
        }
    };

    const translationKey = translationValue[0]?.key ?? '';
    const usedEnumGroupIndices = enumGroups
        .filter((group) => translationKey.includes(`${group.prefix}.`))
        .map((group) => group.index);
    const lowestUsedEnumGroupIndex = usedEnumGroupIndices.length > 0 ? Math.min(...usedEnumGroupIndices) : null;
    const availableEnumGroups = enumGroups.filter(
        (group) =>
            group.values.length > 0 &&
            !translationKey.includes(`${group.prefix}.`) &&
            (lowestUsedEnumGroupIndex === null || group.index <= lowestUsedEnumGroupIndex),
    );
    const selectedGroup = availableEnumGroups.find((group) => group.name === selectedEnumGroup);

    return (
        <form
            style={{ marginBottom: '2rem' }}
            onSubmit={async (event) => {
                await saveInput(event);
            }}
            id={translationValue[0].id}
        >
            <Flex direction="column" id="form-input">
                {translationValue[0]?.project && (
                    <Box marginBottom={5} fontWeight={500}>
                        {translationValue[0].project}
                    </Box>
                )}
                <InputGroup marginBottom={8}>
                    <InputLeftAddon width={55}>Key</InputLeftAddon>
                    <Input
                        value={translationValue[0]?.key}
                        isReadOnly
                        pr="4.5rem"
                        isDisabled
                        id={translationValue[0].id}
                    />
                    <InputRightAddon
                        width="4.5rem"
                        onClick={() => {
                            saveToClipboard(translationValue[0]?.key);
                        }}
                    >
                        <Box style={{ cursor: 'pointer' }}>Copy</Box>
                    </InputRightAddon>
                </InputGroup>
                {translationValue.map((trans: Translation) => (
                    <InputGroup key={trans.value_id || `${trans.id}/${trans.language}`} marginBottom={2} height={10}>
                        <InputLeftAddon>{trans.language_code.toUpperCase()}</InputLeftAddon>
                        <Input
                            id={`${trans.id}/${trans.language}`}
                            value={trans.value ?? ''}
                            placeholder={trans.placeholder}
                            onChange={handleChange}
                        />
                    </InputGroup>
                ))}
                <Flex justify="flex-end" mt={4} id="save-button" gap={2}>
                    <ActionMenu label="Create variation" size="md">
                        {selectedGroup ? (
                            <>
                                <MenuItem
                                    icon={<IoArrowBack />}
                                    closeOnSelect={false}
                                    onClick={() => {
                                        setSelectedEnumGroup(null);
                                    }}
                                >
                                    Back
                                </MenuItem>
                                {selectedGroup.values.length > 0 ? (
                                    selectedGroup.values.map((value) => (
                                        <MenuItem
                                            key={`${selectedGroup.name}-${value}`}
                                            onClick={() => {
                                                openCreateVariationDialog(selectedGroup.prefix, value);
                                            }}
                                        >
                                            {value}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem isDisabled>No values available</MenuItem>
                                )}
                            </>
                        ) : (
                            availableEnumGroups.map((group) => (
                                <MenuItem
                                    key={group.name}
                                    icon={<MdKeyboardArrowRight />}
                                    closeOnSelect={false}
                                    onClick={() => {
                                        setSelectedEnumGroup(group.name);
                                    }}
                                >
                                    {group.name}
                                </MenuItem>
                            ))
                        )}
                    </ActionMenu>
                    <Button
                        colorScheme="blue"
                        type="submit"
                        isDisabled={translationValue.every(
                            (translation: Translation) => translation.value === translation.placeholder,
                        )}
                    >
                        Save
                    </Button>
                </Flex>
                <Modal isOpen={isCreateVariationDialogOpen} onClose={closeCreateVariationDialog} isCentered size="xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create translation variation</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <InputGroup marginBottom={4}>
                                <InputLeftAddon width={55}>Key</InputLeftAddon>
                                <Input value={variationKey} isReadOnly />
                            </InputGroup>

                            {variationDrafts.map((translation) => (
                                <InputGroup
                                    key={translation.value_id || `${translation.id}/${translation.language}`}
                                    marginBottom={2}
                                >
                                    <InputLeftAddon>{translation.language_code.toUpperCase()}</InputLeftAddon>
                                    <Input
                                        value={translation.value ?? ''}
                                        placeholder={translation.placeholder}
                                        onChange={(event) => {
                                            handleVariationValueChange(
                                                translation.id,
                                                translation.language,
                                                event.target.value,
                                            );
                                        }}
                                    />
                                </InputGroup>
                            ))}
                        </ModalBody>
                        <ModalFooter>
                            <Button mr={3} variant="outline" onClick={closeCreateVariationDialog}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={saveVariationTranslations}
                                isLoading={isSavingVariation}
                            >
                                Save
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Flex>
        </form>
    );
};
